import React, { useMemo } from "react";
import Highcharts from "../../lib/highchartsExtended";
import type { Options as HCOptions } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../providers/ThemeProvider";
import type { StopCause } from "../../services/ShiftAnalysisService";
import { formatDuration, buildMachineColorMap } from "./utils";
import Panel from "./Panel";

interface Props {
  causes: StopCause[];
  focusedSn?: string | null;
  onHover?: (sn: string | null) => void;
  onPin?: (sn: string) => void;
}

const DowntimeTreemap: React.FC<Props> = ({
  causes,
  focusedSn,
  onHover,
  onPin,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;

  const options = useMemo<HCOptions>(() => {
    if (causes.length === 0)
      return { chart: { backgroundColor: "transparent" } };

    const uniqueMachines = Array.from(
      new Map(
        causes.map((c) => [c.machine_sn, { machine_sn: c.machine_sn }]),
      ).values(),
    );
    const colorMap = buildMachineColorMap(uniqueMachines);

    // Flat treemap: one block per fault, sized purely by downtime, colored by
    // machine — so the single biggest fault is the biggest block.
    const top = causes.slice(0, 16);
    const points = top.map((c) => {
      const dimmed = focusedSn != null && focusedSn !== c.machine_sn;
      const base = colorMap.get(c.machine_sn) ?? "#6366f1";
      return {
        name: c.fault_text,
        value: c.total_seconds,
        color: dimmed
          ? isDark
            ? "rgba(148,163,184,0.18)"
            : "rgba(15,23,42,0.06)"
          : base,
        custom: {
          sn: c.machine_sn,
          machine: c.machine_name,
          occurrences: c.occurrences,
          durationText: formatDuration(c.total_seconds),
        },
      };
    });

    return {
      chart: {
        backgroundColor: "transparent",
        height: 300,
        spacing: [4, 4, 4, 4],
        animation: { duration: 900 },
      },
      title: undefined,
      credits: { enabled: false },
      tooltip: {
        useHTML: true,
        backgroundColor: isDark ? "#1e293b" : "#ffffff",
        borderRadius: 10,
        style: { color: isDark ? "#f1f5f9" : "#0f172a", fontSize: "12px" },
        pointFormatter() {
          const p = this as unknown as {
            name: string;
            value: number;
            options: {
              custom?: {
                machine?: string;
                occurrences?: number;
                durationText?: string;
              };
            };
          };
          const c = p.options.custom;
          return `<b>${p.name}</b><br/>${c?.machine ?? ""}<br/>${
            c?.durationText ?? formatDuration(p.value)
          }${c?.occurrences ? ` · ${c.occurrences}×` : ""}`;
        },
      },
      series: [
        {
          type: "treemap",
          layoutAlgorithm: "squarified",
          animationLimit: 1000,
          borderRadius: 6,
          borderWidth: 2,
          borderColor: isDark ? "#1e293b" : "#ffffff",
          dataLabels: {
            enabled: true,
            useHTML: true,
            align: "center",
            verticalAlign: "middle",
            style: {
              color: "#ffffff",
              fontSize: "11px",
              fontWeight: "600",
              textOutline: "1px rgba(0,0,0,0.45)",
            },
            formatter() {
              const p = this as unknown as {
                key: string;
                point: {
                  shapeArgs?: { width?: number; height?: number };
                  options: { custom?: { durationText?: string } };
                };
              };
              const w = p.point.shapeArgs?.width ?? 0;
              const h = p.point.shapeArgs?.height ?? 0;
              if (w < 56 || h < 28) return ""; // too small to label
              const dur = p.point.options.custom?.durationText ?? "";
              const name = p.key.length > 24 ? `${p.key.slice(0, 22)}…` : p.key;
              return `<div style="text-align:center;line-height:1.25"><div>${name}</div><div style="opacity:0.9;font-weight:700">${dur}</div></div>`;
            },
          },
          data: points,
          point: {
            events: {
              mouseOver() {
                const p = this as unknown as {
                  options: { custom?: { sn?: string } };
                };
                if (p.options.custom?.sn) onHover?.(p.options.custom.sn);
              },
              mouseOut() {
                onHover?.(null);
              },
              click() {
                const p = this as unknown as {
                  options: { custom?: { sn?: string } };
                };
                if (p.options.custom?.sn) onPin?.(p.options.custom.sn);
              },
            },
          },
        } as any,
      ],
    };
  }, [causes, focusedSn, isDark, onHover, onPin]);

  if (causes.length === 0) return null;

  return (
    <Panel
      label={t("downtimeMap", "Downtime map")}
      meta={t("biggerRectMoreTime", "bigger = more time lost")}
    >
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Panel>
  );
};

export default DowntimeTreemap;
